import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import './MemberRequirementsPopup.css';
import { translateError } from '../utils/errorMessages';
import { downloadRequirementFile } from '../utils/requirementFiles';

interface RequirementType {
  _id: string;
  name: string;
  code: string;
  description?: string;
  inputType: 'boolean' | 'text' | 'file' | 'date' | 'select';
  options?: string[];
  templateFile?: string;
  templateFileName?: string;
  isSystem: boolean;
}

interface MemberRequirement {
  _id: string;
  requirementType: RequirementType;
  status: 'pending' | 'submitted' | 'approved' | 'rejected';
  value?: string;
  submittedAt?: string;
  reviewedBy?: { _id: string; name: string };
  reviewedAt?: string;
  notes?: string;
}

interface EventRequirement {
  _id: string;
  requirementType: RequirementType;
  isRequired: boolean;
  dueDate?: string;
  description?: string;
}

interface MemberRequirementsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
  eventId: string;
  eventName?: string;
  onUpdate?: () => void;
}

const MemberRequirementsPopup: React.FC<MemberRequirementsPopupProps> = ({
  isOpen,
  onClose,
  memberId,
  memberName,
  eventId,
  eventName,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [eventRequirements, setEventRequirements] = useState<EventRequirement[]>([]);
  const [memberRequirements, setMemberRequirements] = useState<MemberRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);

  // Form state for each requirement
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  // Pending file uploads (file objects waiting to be uploaded on submit)
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  // Track which requirement is being rejected (to show rejection reason input)
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  // Rejection reason text
  const [rejectionReason, setRejectionReason] = useState<string>('');

  useEffect(() => {
    if (isOpen && memberId && eventId) {
      fetchRequirements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, memberId, eventId]);

  const fetchRequirements = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch event requirements and member requirements in parallel.
      // Admins keep the gated admin routes; members read both the event
      // requirement DEFINITIONS and their own member requirements via /api/my
      // (the admin routes 403 for members). This popup is only mounted from the
      // admin AttendanceOverview today, but the branch keeps it member-safe if
      // reused on member pages.
      const [eventReqResponse, memberReqResponse] = await Promise.all([
        isAdmin
          ? apiClient.get<EventRequirement[]>(`/api/requirements/events/${eventId}/requirements`)
          : api.my.eventRequirements.list(eventId),
        isAdmin
          ? apiClient.get<MemberRequirement[]>(`/api/requirements/members/${memberId}/requirements?event=${eventId}`)
          : api.my.memberRequirements.list(memberId, eventId),
      ]);

      const memberReqData = memberReqResponse.data as MemberRequirement[];
      setEventRequirements(eventReqResponse.data);
      setMemberRequirements(memberReqData);

      // Initialize form values from existing submissions
      const initialValues: Record<string, string> = {};
      memberReqData.forEach(mr => {
        if (mr.value) {
          initialValues[mr.requirementType._id] = mr.value;
        }
      });
      setFormValues(initialValues);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const getMemberRequirement = (requirementTypeId: string): MemberRequirement | undefined => {
    return memberRequirements.find(mr => mr.requirementType._id === requirementTypeId);
  };

  const getStatusBadgeClass = (status?: string): string => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'submitted':
        return 'status-submitted';
      case 'rejected':
        return 'status-rejected';
      case 'pending':
      default:
        return 'status-pending';
    }
  };

  const handleInputChange = (requirementTypeId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [requirementTypeId]: value,
    }));
  };

  const handleSubmit = async (requirementTypeId: string) => {
    try {
      setSubmitting(requirementTypeId);
      setError(null);

      let value = formValues[requirementTypeId] || '';

      // If there's a pending file, upload it first
      const pendingFile = pendingFiles[requirementTypeId];
      if (pendingFile) {
        const formData = new FormData();
        formData.append('file', pendingFile);

        const uploadResponse = isAdmin
          ? await apiClient.post<{ fileUrl: string; fileName: string }>(
              `/api/requirements/members/${memberId}/requirements/upload`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } },
            )
          : await api.my.memberRequirements.upload(memberId, formData);

        // Use the uploaded file URL as the value
        value = (uploadResponse.data as { fileUrl: string; fileName: string }).fileUrl;

        // Clear the pending file
        setPendingFiles(prev => {
          const updated = { ...prev };
          delete updated[requirementTypeId];
          return updated;
        });
      }

      if (isAdmin) {
        await apiClient.post(`/api/requirements/members/${memberId}/requirements`, {
          requirementTypeId,
          eventId,
          value,
        });
      } else {
        await api.my.memberRequirements.submit(memberId, {
          requirementTypeId,
          eventId,
          value,
        });
      }

      await fetchRequirements();
      if (onUpdate) onUpdate();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSubmitting(null);
    }
  };

  const handleApprove = async (memberRequirementId: string) => {
    try {
      setSubmitting(memberRequirementId);
      setError(null);

      await apiClient.put(`/api/requirements/members/${memberId}/requirements/${memberRequirementId}`, {
        status: 'approved',
      });

      await fetchRequirements();
      if (onUpdate) onUpdate();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSubmitting(null);
    }
  };

  const handleReject = async (memberRequirementId: string) => {
    if (!rejectionReason.trim()) {
      setError(t('requirements.rejectionReasonRequired'));
      return;
    }

    try {
      setSubmitting(memberRequirementId);
      setError(null);

      await apiClient.put(`/api/requirements/members/${memberId}/requirements/${memberRequirementId}`, {
        status: 'rejected',
        notes: rejectionReason.trim(),
      });

      // Reset rejection state
      setRejectingId(null);
      setRejectionReason('');

      await fetchRequirements();
      if (onUpdate) onUpdate();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSubmitting(null);
    }
  };

  const handleStartReject = (memberRequirementId: string) => {
    setRejectingId(memberRequirementId);
    setRejectionReason('');
    setError(null);
  };

  const handleCancelReject = () => {
    setRejectingId(null);
    setRejectionReason('');
  };

  // Submissions are behind an authenticated route now, so a plain href to the
  // stored /uploads path no longer resolves — fetch the bytes with the token.
  const handleDownload = async (requirementId: string) => {
    try {
      await downloadRequirementFile(memberId, requirementId, !isAdmin);
    } catch (err) {
      setError(translateError(err, t));
    }
  };

  const renderInputField = (eventReq: EventRequirement, memberReq?: MemberRequirement) => {
    const reqType = eventReq.requirementType;
    const reqTypeId = reqType._id;
    const currentValue = formValues[reqTypeId] || '';
    const isDisabled = memberReq?.status === 'approved' || submitting === reqTypeId;

    switch (reqType.inputType) {
      case 'boolean':
        return (
          <div className="requirement-input-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={currentValue === 'true'}
                onChange={(e) => handleInputChange(reqTypeId, e.target.checked ? 'true' : 'false')}
                disabled={isDisabled}
              />
              <span>{t('requirements.confirm')}</span>
            </label>
          </div>
        );

      case 'text':
        return (
          <div className="requirement-input-group">
            <textarea
              value={currentValue}
              onChange={(e) => handleInputChange(reqTypeId, e.target.value)}
              placeholder={t('requirements.enterText')}
              disabled={isDisabled}
              rows={3}
            />
          </div>
        );

      case 'file':
        const pendingFile = pendingFiles[reqTypeId];
        const hasUploadedFile = currentValue && currentValue.startsWith('/');
        return (
          <div className="requirement-input-group">
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  // Store the file object for upload on submit
                  setPendingFiles(prev => ({ ...prev, [reqTypeId]: file }));
                  // Store filename for display
                  handleInputChange(reqTypeId, file.name);
                }
              }}
              disabled={isDisabled}
            />
            {pendingFile && (
              <span className="file-name">{t('requirements.fileSelected')}: {pendingFile.name}</span>
            )}
            {hasUploadedFile && !pendingFile && memberReq && (
              <a
                href="#"
                className="uploaded-file-link"
                onClick={(e) => { e.preventDefault(); handleDownload(memberReq._id); }}
              >
                {t('requirements.downloadUploaded')}
              </a>
            )}
          </div>
        );

      case 'date':
        return (
          <div className="requirement-input-group">
            <input
              type="date"
              value={currentValue}
              onChange={(e) => handleInputChange(reqTypeId, e.target.value)}
              disabled={isDisabled}
            />
          </div>
        );

      case 'select':
        return (
          <div className="requirement-input-group">
            <select
              value={currentValue}
              onChange={(e) => handleInputChange(reqTypeId, e.target.value)}
              disabled={isDisabled}
            >
              <option value="">{t('requirements.selectOption')}</option>
              {reqType.options?.map((opt, idx) => (
                <option key={idx} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );

      default:
        return null;
    }
  };

  const renderRequirementItem = (eventReq: EventRequirement) => {
    const memberReq = getMemberRequirement(eventReq.requirementType._id);
    const status = memberReq?.status;
    const canSubmit = !status || status === 'pending' || status === 'rejected';
    const uploadedFileUrl = memberReq?.value && memberReq.value.startsWith('/') ? memberReq.value : null;

    return (
      <div key={eventReq._id} className="requirement-item">
        <div className="requirement-header">
          <div className="requirement-info">
            <span className="requirement-name">
              {eventReq.requirementType.name}
              {eventReq.isRequired && <span className="required-badge">*</span>}
            </span>
            {eventReq.requirementType.description && (
              <span className="requirement-description">{eventReq.requirementType.description}</span>
            )}
            {eventReq.dueDate && (
              <span className="requirement-due-date">
                {t('requirements.dueBy')}: {new Date(eventReq.dueDate).toLocaleDateString()}
              </span>
            )}
            {uploadedFileUrl && memberReq && (
              <a
                href="#"
                className="uploaded-file-link"
                onClick={(e) => { e.preventDefault(); handleDownload(memberReq._id); }}
              >
                {t('requirements.downloadUploaded')}
              </a>
            )}
          </div>
          <span className={`requirement-status ${getStatusBadgeClass(status)}`}>
            {status ? t(`requirements.status.${status}`) : t('requirements.status.pending')}
          </span>
        </div>

        <div className="requirement-body">
          {renderInputField(eventReq, memberReq)}

          {memberReq?.notes && status === 'rejected' && (
            <div className="rejection-notes">
              <strong>{t('requirements.rejectionReason')}:</strong> {memberReq.notes}
            </div>
          )}

          <div className="requirement-actions">
            {canSubmit && !isAdmin && (
              <button
                type="button"
                className="submit-btn"
                onClick={() => handleSubmit(eventReq.requirementType._id)}
                disabled={submitting === eventReq.requirementType._id}
              >
                {submitting === eventReq.requirementType._id
                  ? t('common.saving')
                  : status === 'rejected'
                    ? t('requirements.resubmit')
                    : t('requirements.submit')}
              </button>
            )}

            {isAdmin && memberReq && status === 'submitted' && (
              <>
                {rejectingId === memberReq._id ? (
                  <div className="rejection-form">
                    <textarea
                      className="rejection-reason-input"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder={t('requirements.rejectionReasonPlaceholder')}
                      rows={2}
                      autoFocus
                    />
                    <div className="rejection-form-actions">
                      <button
                        type="button"
                        className="reject-confirm-btn"
                        onClick={() => handleReject(memberReq._id)}
                        disabled={submitting === memberReq._id || !rejectionReason.trim()}
                      >
                        {submitting === memberReq._id ? t('common.saving') : t('requirements.confirmReject')}
                      </button>
                      <button
                        type="button"
                        className="reject-cancel-btn"
                        onClick={handleCancelReject}
                        disabled={submitting === memberReq._id}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="approve-btn"
                      onClick={() => handleApprove(memberReq._id)}
                      disabled={submitting === memberReq._id}
                    >
                      {submitting === memberReq._id ? t('common.saving') : t('requirements.approve')}
                    </button>
                    <button
                      type="button"
                      className="reject-btn"
                      onClick={() => handleStartReject(memberReq._id)}
                      disabled={submitting === memberReq._id}
                    >
                      {t('requirements.reject')}
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          {memberReq?.reviewedBy && memberReq.reviewedAt && (
            <div className="review-info">
              {t('requirements.reviewedBy')} {memberReq.reviewedBy.name}
              {' '}{t('requirements.on')} {new Date(memberReq.reviewedAt).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('requirements.memberRequirements')}: ${memberName}`}
      size="medium"
    >
      <div className="member-requirements-popup">
        {eventName && (
          <div className="event-context">
            <strong>{t('requirements.forEvent')}:</strong> {eventName}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : eventRequirements.length === 0 ? (
          <div className="no-requirements">{t('requirements.noRequirements')}</div>
        ) : (
          <div className="requirements-list">
            {eventRequirements.map(renderRequirementItem)}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MemberRequirementsPopup;
